import {
  appointmentSlot,
  INSPECTION_RESULT_LABELS,
  type InquiryStatus,
  type InspectionResult,
} from '@ghp/domain'
import type { BadgeTone } from '@ghp/ui'

import type { OutboxState } from '../../offline/data-db'
import type { PullResult } from '../../offline/pull'
import type { TourState } from './lib/list-filters'

/**
 * Les libellés d'écran du domaine, **nommés une fois**. La liste et l'éditeur affichent les
 * mêmes états ; deux tables de correspondance divergeraient au premier renommage.
 *
 * Les identifiants restent en anglais, les libellés en français : c'est la langue du métier
 * et celle du prototype validé (`/CLAUDE.md`, garde-fou 2).
 */

interface Presentation {
  label: string
  tone: BadgeTone
}

/**
 * `Inquiry.status` (M3) vu par l'agent. Les mots sont ceux du prototype terrain — « À
 * planifier », « Planifié », « En cours », « Transmis » — complétés par les étapes qui se
 * passent après lui et qu'il peut néanmoins recevoir en tournée.
 *
 * ⚠️ **Non exportée** : aucun écran n'affiche `Inquiry.status` tel quel, et c'est délibéré
 * (#181). Les deux qui montrent un état — la liste et la fiche — lisent
 * {@link TOUR_STATE_PRESENTATION}, dont cette table est la base.
 */
const STATUS_PRESENTATION: Record<InquiryStatus, Presentation> = {
  received: { label: 'À planifier', tone: 'info' },
  assigned: { label: 'Planifié', tone: 'info' },
  in_progress: { label: 'En cours', tone: 'warn' },
  submitted: { label: 'Transmis', tone: 'ok' },
  validated: { label: 'Validé', tone: 'ok' },
  archived: { label: 'Archivé', tone: 'info' },
  dropped: { label: 'Abandonné', tone: 'bad' },
}

/**
 * L'état d'un contrôle **dans la tournée** (`lib/list-filters.ts`) : les sept du cycle de vie,
 * plus le renvoi au terrain et l'attente de créneau.
 *
 * ⚠️ C'est cette table que lisent la colonne ÉTAT et la pastille de la fiche, jamais
 * {@link STATUS_PRESENTATION} : le statut en cache reste « Planifié » sur un contrôle que le
 * bureau a déjà reçu, l'API cessant de servir la demande une fois déposée (#181).
 *
 * « Renvoyé par le bureau » et non « Renvoyé » tout court : sur une tablette, « renvoyé » se lit
 * aussi bien « réexpédié par l'agent ». C'est le pendant du « Renvoyer au terrain » du
 * back-office, dit du côté qui le reçoit.
 */
export const TOUR_STATE_PRESENTATION: Record<TourState, Presentation> = {
  ...STATUS_PRESENTATION,
  returned: { label: 'Renvoyé par le bureau', tone: 'warn' },
  // Un contrôle libre transmis : le bureau doit encore le rattacher à une demande (#419).
  unattached: { label: 'En attente de rattachement', tone: 'warn' },
  // Les mots de `received`, et non deux formulations pour un même fait : que le bureau n'ait
  // affecté personne ou qu'il ait affecté l'équipe sans poser de créneau, ce que l'agent lit est
  // le même — il n'y a pas d'heure à laquelle se présenter. C'est l'`aPlanifier` du prototype.
  unscheduled: STATUS_PRESENTATION.received,
  // Libellé et ton de la frame validée Terrain 2.8 (le prototype n'en fixait pas le ton).
  passage_notice: { label: 'Avis de passage', tone: 'warn' },
}

/**
 * Les trois conclusions, dans les mots de GHP — ceux du bureau, du rapport et de l'export client
 * (`INSPECTION_RESULT_LABELS`) : seul le ton est d'ici.
 *
 * Trois écrans la lisent — l'onglet « Contrôle », où la conclusion se pose, l'onglet
 * « Transmettre », qui la récapitule avant l'envoi, et l'historique, qui montre celle qui est
 * partie. Recopiée chez l'un d'eux, elle finirait par dire autre chose que les deux autres.
 */
export const RESULT_PRESENTATION: Record<InspectionResult, Presentation> = {
  compliant: { label: INSPECTION_RESULT_LABELS.compliant, tone: 'ok' },
  partial: { label: INSPECTION_RESULT_LABELS.partial, tone: 'warn' },
  non_compliant: { label: INSPECTION_RESULT_LABELS.non_compliant, tone: 'bad' },
}

/**
 * L'état de la file de propositions, **tel que l'agent doit le lire**. Rien de silencieux :
 * un envoi qui a échoué se voit, et un envoi qui attend le réseau se distingue d'un envoi
 * refusé — le premier repartira tout seul, le second demande un appel au bureau.
 */
export const SYNC_PRESENTATION: Record<OutboxState, Presentation> = {
  pending: { label: "En attente d'envoi", tone: 'warn' },
  sending: { label: 'Envoi…', tone: 'info' },
  sent: { label: 'Transmis', tone: 'ok' },
  failed: { label: 'En échec', tone: 'bad' },
  rejected: { label: 'Refusé — non enregistré', tone: 'bad' },
}

/**
 * Le motif d'une descente, traduit en conduite à tenir — la même pour l'agent quelle que soit
 * la donnée descendue : attendre le réseau (`unreachable`), refaire sa connexion (`refused`),
 * ou prévenir le bureau (`faulty`). Tournée et référentiel échouent pour les mêmes raisons ;
 * deux classements divergeraient au premier motif ajouté.
 */
export type PullPresentation = 'done' | 'refused' | 'unreachable' | 'faulty'

export function pullPresentationOf(result: PullResult): PullPresentation {
  // `partial` veut dire qu'une partie est arrivée : ce qui est descendu est rangé, et c'est
  // l'indicateur « prêt pour le terrain » (D22 pt.3), pas un état d'échec, qui dira l'incomplet.
  if (result.outcome !== 'unavailable') return 'done'

  switch (result.reason) {
    case 'refused':
      return 'refused'
    case 'malformed':
      return 'faulty'
    // `no_session` — session dégradée, rien n'a même été demandé. Pour l'agent c'est la même
    // attente que le sous-sol : la session remonte en ligne d'elle-même au retour du réseau.
    case 'offline':
    case 'no_session':
      return 'unreachable'
  }
}

/**
 * « 04/08/2026 · 08:30 » — l'horodatage du prototype, ⚠️ à l'heure de Paris et jamais de la
 * tablette, qui peut être mal réglée. `null` sans date lisible : chaque écran choisit son repli.
 */
export function stampLabel(instant: string | null): string | null {
  const slot = instant === null ? null : appointmentSlot(instant)

  return slot === null ? null : `${slot.day} · ${slot.time}`
}
